import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  updateProfile,
  User,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { create } from 'zustand';

import { auth, db } from '../config';
import { PermissionsInterface, UserDataFirebaseResponse } from '../types';
import { fetchLanguages } from './utils';

export type CurrentUser = User & UserDataFirebaseResponse;

interface AuthState {
  currentUser: any | null;
  isAdmin: boolean;
  isLoading: boolean;
  signInWithGoogle: () => Promise<User>;
  signInWithEmail: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  updateUserProfile: (data: {
    displayName?: string;
    photoURL?: string;
    name?: string;
    surname?: string;
    phoneNumber?: string;
    role?: string;
  }) => Promise<void>;
  changePassword: (newPassword: string) => Promise<void>;
  defaultLang: string;
  permissions: PermissionsInterface[];
}

export const useAuthState = create<AuthState>((set) => ({
  currentUser: null,
  isAdmin: false,
  isLoading: true,
  defaultLang: 'pl',
  permissions: [],

  signInWithGoogle: async () => {
    set({ isLoading: true });
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        hd: 'hotelspot.pl',
      });

      const result = await signInWithPopup(auth, provider);
      const { user } = result;

      if (!user.email || !user.email.endsWith('@hotelspot.pl')) {
        await signOut(auth);
        throw new Error('Tylko konta z domeny hotelspot.pl są dozwolone');
      }

      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const displayNameParts = user.displayName?.split(' ') || [];
      const firstName = displayNameParts[0] || '';
      const lastName = displayNameParts.slice(1).join(' ') || '';

      if (!userDocSnap.exists()) {
        await setDoc(userDocRef, {
          associated_organization: '',
          organizations: [],
          email: user.email,
          name: firstName,
          surname: lastName,
          phoneNumber: user.phoneNumber || '',
          firstLogin: false,
          isStore: false,
          permissions: [],
          createdAt: serverTimestamp(),
          temporaryPasswordSet: false,
          photoURL: user.photoURL || '',
          displayName: user.displayName || '',
          isAdmin: true,
        });
      } else {
        await updateDoc(userDocRef, {
          email: user.email,
          photoURL: user.photoURL || '',
          displayName: user.displayName || '',
        });
      }

      return user;
    } catch (error) {
      set({ isLoading: false });
      // eslint-disable-next-line no-console
      console.error('Error signing in with Google:', error);
      throw error;
    }
  },

  signInWithEmail: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      // Bez ograniczenia do domeny hotelspot.pl — do ACS logują się też konta
      // adminów zakładane w panelu (dowolny adres). Liczy się uprawnienie, nie
      // kształt e-maila.
      const result = await signInWithEmailAndPassword(auth, email, password);
      const { user } = result;

      // Warunek lustrzany do backendowego `verifyAdminAccess`: konto z domeny
      // hotelspot.pl ALBO `isAdmin`/`isHotelspotAdmin`. Claim sprawdzamy pierwszy
      // (nie kosztuje odczytu), a dokument dopiero gdy claimu nie ma — konta
      // sprzed wprowadzenia claimów mają flagę tylko w `users/{uid}`.
      const tokenResult = await user.getIdTokenResult();
      const hasAdminClaim = tokenResult.claims.isAdmin === true
        || tokenResult.claims.isHotelspotAdmin === true;

      let isAllowed = hasAdminClaim || (user.email?.endsWith('@hotelspot.pl') ?? false);

      if (!isAllowed) {
        const userDocSnap = await getDoc(doc(db, 'users', user.uid));
        isAllowed = userDocSnap.exists()
          && (userDocSnap.get('isAdmin') === true || userDocSnap.get('isHotelspotAdmin') === true);
      }

      if (!isAllowed) {
        await signOut(auth);
        throw new Error('This account has no access to the Admin Center');
      }

      return user;
    } catch (error) {
      set({ isLoading: false });
      console.error('Error signing in with email:', error);
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await signOut(auth);
      set({
        currentUser: null,
        isAdmin: false,
      });
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  },

  updateUserProfile: async (data) => {
    set({ isLoading: true });
    try {
      const user = auth.currentUser;
      if (user) {
        await updateProfile(user, {
          displayName: data.displayName,
          photoURL: data.photoURL,
        });
        await updateDoc(doc(db, 'users', user.uid), {
          name: data.name,
          surname: data.surname,
          phoneNumber: data.phoneNumber,
          role: data.role,
          photoURL: data.photoURL,
          displayName: data.displayName,
        });
        useAuthState.setState((state) => ({
          currentUser: {
            ...state.currentUser,
            ...data,
            photoURL: data.photoURL,
            displayName: data.displayName,
          },
          isLoading: false,
        }));
      }
    } catch (error) {
      set({ isLoading: false });
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  changePassword: async (newPassword: string) => {
    set({ isLoading: true });
    try {
      const user = auth.currentUser;
      if (user) {
        await updatePassword(user, newPassword);
        set({ isLoading: false });
      }
    } catch (error) {
      set({ isLoading: false });
      console.error('Error changing password:', error);
      throw error;
    }
  },
}));

onAuthStateChanged(auth, async (user) => {
  useAuthState.setState({ isLoading: true });

  await fetchLanguages();

  if (user) {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.data();
      const isAdmin = userDocSnap.exists() && userData?.isAdmin;

      useAuthState.setState({
        currentUser: { ...user, id: user.uid, ...userData },
        isAdmin,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      useAuthState.setState({
        currentUser: null,
        isAdmin: false,
        isLoading: false,
      });
    }
  } else {
    useAuthState.setState({
      currentUser: null,
      isAdmin: false,
      isLoading: false,
    });
  }
});
